import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from 'tss-react/mui';
import { Modal, Fade, Backdrop, Tooltip, Switch } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

// The dialog of the Admin lists (a day of the time frame, a holiday) and of
// Send email: a header with an icon and a title, the fields, and the buttons
// in a footer.

const useStyles = makeStyles()((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  paper: {
    // Positioned so it stacks above the modal's backdrop.
    position: "relative",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: 440,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    boxShadow: theme.shadows[8],
    outline: "none",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2.5, 1.5, 1, 3),
  },
  badge: {
    flexShrink: 0,
    width: 44,
    height: 44,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    backgroundColor: theme.palette.primary.light,
  },
  // For a dialog that confirms something destructive.
  badgeDanger: {
    color: theme.palette.tone.danger.main,
    backgroundColor: theme.palette.tone.danger.soft,
  },
  heading: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 20,
    fontWeight: 500,
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: 13,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  close: {
    alignSelf: "flex-start",
    color: theme.palette.text.secondary,
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3, 1),
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    padding: theme.spacing(2, 3, 2.5),
  },
  // A setting with its switch at the end of the line.
  switchRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 0.5, 0.75, 1.5),
    borderRadius: 10,
    backgroundColor: theme.palette.action.hover,
    cursor: "pointer",
  },
  switchRowDisabled: {
    cursor: "default",
    opacity: 0.6,
  },
  switchText: {
    flex: 1,
    minWidth: 0,
  },
  switchLabel: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
  },
  switchHint: {
    fontSize: 12.5,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
}));

export const DialogSwitchRow = ({ label, hint, checked, disabled = false, onChange }) => {
  const { classes, cx } = useStyles();
  return (
    <label className={cx(classes.switchRow, disabled && classes.switchRowDisabled)}>
      <span className={classes.switchText}>
        <div className={classes.switchLabel}>{label}</div>
        {hint && <div className={classes.switchHint}>{hint}</div>}
      </span>
      <Switch color="primary" checked={!!checked} disabled={disabled} onChange={onChange} />
    </label>
  );
};

DialogSwitchRow.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const AdminDialog = ({ open, onClose, icon, title, subtitle, children, footer, maxWidth, tone }) => {
  const { classes, cx } = useStyles();
  return (
    <Modal
      aria-labelledby="admin-dialog-title"
      className={classes.modal}
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{
        backdrop: Backdrop
      }}
      slotProps={{
        backdrop: {
          timeout: 300,
        }
      }}
    >
      <Fade in={open}>
        <div className={classes.paper} style={maxWidth ? { maxWidth } : undefined}>
          <div className={classes.header}>
            <div className={cx(classes.badge, tone === "danger" && classes.badgeDanger)}>{icon}</div>
            <div className={classes.heading}>
              <div id="admin-dialog-title" className={classes.title}>{title}</div>
              {subtitle && <div className={classes.subtitle}>{subtitle}</div>}
            </div>
            <Tooltip title="Close">
              <IconButton aria-label="close" className={classes.close} size="small" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </div>
          <div className={classes.body}>{children}</div>
          <div className={classes.footer}>{footer}</div>
        </div>
      </Fade>
    </Modal>
  );
};

AdminDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  icon: PropTypes.node,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.node,
  maxWidth: PropTypes.number,
  tone: PropTypes.oneOf(["danger"]),
};

export default AdminDialog;
